import React, { useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import InnerHTML from '@Components/InnerHTML';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import PrepareTrackingDataHelper from '@Services/PrepareTrackingDataHelper';
import { citationLiteratureFormatter, patentFamilyFormatter } from '@Services/FormatUtils';
import CitedCountLink from '@Components/Results/CitedCountLink';
import DocumentButtons from '@Components/Results/documents/DocumentButtons';
import ListOfAuthors from '@Components/Results/reactions/reaction-details/ListOfAuthors';
import DocumentCollapsibleContent from '@Components/Results/documents/DocumentCollapsibleContent';
import DocumentJournals from '@Components/Results/documents/DocumentJournals';
import { getValueByNs } from '@Hooks/LibraryInitialization/MoreUnderscore';
import { DOCUMENT_PROCESSING_TYPES } from '@Containers/ResultsFilters/ResultsFiltersConstants';
import Auth from '@Services/Auth';
import { FEATURES } from '@Constants/rx-ui/constants';
import { getDocumentNoAuthorLabel } from '@Components/Results/reactions/reaction-details/ReactionCitations';
import Catalyst from '@Services/Catalyst';
import useExclusiveDetails from '@Components/Results/common/useExclusiveDetails';
import { getFirstAvailableSection, getDetailNavDataE2e } from '@Components/Results/documents/DocumentExpandSections';
import InSummaryPillContainer from '@Containers/Summary/InSummaryPillContainer';
import { sanitizeDiscard } from '@Services/HtmlSanitizeService';

import './Document.scss';
import { Button } from '@els/els-react--button';

function Document(props) {
  const { metadata, document: citation, zoom: zoomValue, context } = props;
  // Exclusive details navigation: at most one category open at a time (like Substance).
  // When nothing is open, the matched-text hit cards render as the default view.
  const { activeId, toggle, open, close } = useExclusiveDetails();
  const navRef = useRef(null);

  // Closing a card returns focus to that section's nav button in THIS result
  // (scoped via navRef), matching the nav-toggle behaviour.
  const handleClose = useCallback(() => {
    const closingId = activeId;
    close();
    const closingDataE2e = getDetailNavDataE2e(closingId);
    if (closingDataE2e) {
      const navTarget = navRef.current?.querySelector(`[data-e2e="${closingDataE2e}"]`);
      (navTarget?.closest('button') || navTarget)?.focus();
    }
  }, [activeId, close]);

  useEffect(() => {
    async function updateUrls() {
      if (citation.issueCitedByCounter && citation.issueCitedByCounter > 0) {
        await Catalyst.appendVisitorIDToUrl(citation.citedBy);
      }
    }
    updateUrls();
  }, [citation.issueCitedByCounter, citation.citedBy]);

  const firstSection = useMemo(() => getFirstAvailableSection(citation), [citation]);

  // Strip markup (e.g. <hi>) so the accessible name doesn't leak HTML tags to screen readers.
  const accessibleTitle = useMemo(() => sanitizeDiscard(citation.title || 'No title', { allowedTags: [] }), [citation.title]);

  const isPatent = useCallback(() => /patent/gi.test(getValueByNs(props, 'document.type')), [props]);

  const getTrackingAttrs = useCallback(
    (trackingAttrs, prepare = true) => ({
      'data-click-delegation': 'true',
      'data-tracking': 'true',
      'data-tracking-context': context || get(metadata, 'context'),
      'data-tracking-preview': getValueByNs(props, 'metadata.isViewPreview'),
      'data-tracking-track-link-location-in-results': true,
      'data-tracking-from-rt': metadata.fromRT,
      ...trackingAttrs,
      ...(prepare ? PrepareTrackingDataHelper.prepare(citation) : {}),
    }),
    [context, metadata, citation, props]
  );

  // Clicking the title toggles the first available detail category (unless the
  // user is selecting text within it).
  const titleClickToggleSection = useCallback(
    (e) => {
      if (!firstSection) return;
      const target = e.currentTarget;
      e.preventDefault();
      setTimeout(() => {
        if (!Document.checkIfClickTargetContainsSelection(target)) {
          toggle(firstSection);
        }
      }, 0);
    },
    [firstSection, toggle]
  );

  const isFirstSectionActive = activeId === firstSection;
  const zoom = metadata.isViewPreview ? 0 : zoomValue;
  const processingMode =
    Auth.isFeatureEnabled(FEATURES.enableRccDocumentProcessedTypeLabel) && DOCUMENT_PROCESSING_TYPES.getType(citation.processingMode);

  return (
    <div className="u-els-display-flex u-els-margin-left u-els-font-size-meta document-content-container">
      <div className="document-content u-els-display-flex">
        <div className="document-element">
          <Button
            data-e2e="document-title-button"
            type="link"
            className="e2e-document-action-title c-els-link--block document-title-selectable"
            tabIndex={firstSection ? '0' : '-1'}
            aria-label={`Toggle details for ${accessibleTitle}`}
            onClick={titleClickToggleSection}
            {...getTrackingAttrs({
              'data-tracking': !isFirstSectionActive,
              'data-tracking-type': 'contentlinkclick',
              'data-tracking-action': 'title',
            })}>
            {citation.title ? (
              <InnerHTML
                tag="h4"
                className="u-els-font-family-serif u-els-color-text u-els-display-inline c-els-link__text"
                html={citation.title}
              />
            ) : (
              <h4 className="u-els-font-family-serif u-els-color-text u-els-display-inline c-els-link__text">No title</h4>
            )}
          </Button>
          <div className="o-els-flex-layout o-els-flex-layout--left o-els-flex-layout--middle u-els-font-size-base">
            <InSummaryPillContainer
              citation={citation}
              databaseId={metadata?.databaseId}
              resultName={metadata?.resultName}
              className="o-els-flex-layout__item--top u-els-margin-right"
            />
            {citation.issueCitedByCounter > 0 && (
              <CitedCountLink
                data-e2e="document-cited-count-link"
                className="e2e-cited-count"
                citedCount={citation.issueCitedByCounter}
                citedUrl={citation.citedBy}
                {...getTrackingAttrs(
                  {
                    'data-tracking-type': 'linkout',
                    'data-tracking-action': 'cited',
                  },
                  true
                )}
              />
            )}
          </div>
          <div className="u-els-font-size-meta">
            {isPatent() && citation.assignees && (
              <>
                {!Auth.isFeatureEnabled(FEATURES.disablePatentSightUIChanges) && <span>Current Patent Assignee: </span>}
                <InnerHTML tag="span" className="rx-element-assignee" html={citation.assignees + (citation.authors ? '; ' : '')} />
              </>
            )}
            {(() => {
              let authorsContent;
              if (citation.authors) {
                authorsContent = <ListOfAuthors metadata={metadata} citation={citation} />;
              } else if (citation.assignees) {
                authorsContent = null;
              } else {
                authorsContent = getDocumentNoAuthorLabel(citation.year);
              }
              return <span className="rx-element-authors">{authorsContent}</span>;
            })()}{' '}
            {/* for patent */}
            {isPatent() && (
              <InnerHTML
                tag="span"
                className="rx-element-literature"
                html={`- ${Document.literatureFormatter(citation.citationPatents)}`}
              />
            )}
          </div>
          {!isEmpty(citation.citationJournals) && (
            <DocumentJournals journals={citation.citationJournals} doi={citation.doi} withDoiLink className="u-els-margin-top-1o2" />
          )}
          {/* again for patent */}
          {isPatent() && citation.patentFamily && (
            <div className="u-els-font-size-meta">
              <span className="rx-element-literature-title">Patent Family Members: </span>
              <InnerHTML tag="span" html={Document.patentFormatter(citation.patentFamily)} />
            </div>
          )}
          <DocumentButtons
            document={citation}
            activeId={activeId}
            onToggle={toggle}
            onOpen={open}
            onClose={close}
            getTrackingAttrs={getTrackingAttrs}
            metadata={metadata}
            navRef={navRef}
          />
        </div>

        {processingMode && (
          <div className="u-els-margin-left-1o2 u-els-margin-right-1o2 u-els-width-1o12">
            <div className="rx-element-processing-mode e2e-processing-mode">
              <svg className="els-btn-icon rx-border-none els-btn-icon-sm processing-mode-icon">
                <use xlinkHref={`#icon-${processingMode.icon}`} />
              </svg>
              {processingMode.label}
            </div>
          </div>
        )}
      </div>

      <DocumentCollapsibleContent
        metadata={metadata}
        document={citation}
        zoom={zoom}
        activeId={activeId}
        onToggle={toggle}
        onClose={handleClose}
        getTrackingAttrs={getTrackingAttrs}
      />
    </div>
  );
}

Document.literatureFormatter = (content) => citationLiteratureFormatter(content);
Document.patentFormatter = (content) => patentFamilyFormatter(content);
Document.checkIfClickTargetContainsSelection = (target) => {
  const selection = window.getSelection();
  const isSelected = !!selection.toString();
  const selectedNode = getValueByNs(selection, 'anchorNode.parentNode');
  return isSelected && selectedNode && target.contains(selectedNode);
};

Document.propTypes = {
  metadata: MetadataPropTypesShape,
  document: PropTypes.object.isRequired,
  zoom: PropTypes.number,
  context: PropTypes.string,
};

export default Document;
